import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Ticket,
  Plus,
  Copy,
  Eye,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// ============================================================================
// TYPES
// ============================================================================

interface InviteCode {
  id: string;
  code: string;
  label: string;
  maxUses: number;
  usedCount: number;
  remaining: number;
  expiresAt: string | null;
  active: boolean;
  createdBy: string;
  createdAt: string;
}

interface InviteCodeListResponse {
  codes: InviteCode[];
  total: number;
  page: number;
  perPage: number;
}

interface InviteCodeUse {
  id: string;
  projectId: string;
  projectName: string;
  projectUf: string;
  ipAddress: string;
  usedAt: string;
}

interface InviteCodeUsesResponse {
  code: string;
  label: string;
  uses: InviteCodeUse[];
  total: number;
}

interface CreateInviteCodeForm {
  label: string;
  maxUses: number;
  code: string;
  expiresAt: string;
}

// ============================================================================
// API HELPERS
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

function getHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  };
}

async function fetchInviteCodes(): Promise<InviteCodeListResponse> {
  const res = await fetch(`${API_URL}/v1/admin/invite-codes`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Erro ao buscar códigos: ${res.status}`);
  return res.json();
}

async function createInviteCode(data: {
  label: string;
  maxUses: number;
  code?: string;
  expiresAt?: string;
}): Promise<InviteCode> {
  const body: Record<string, unknown> = {
    label: data.label,
    maxUses: data.maxUses,
  };
  if (data.code) body.code = data.code;
  if (data.expiresAt) body.expiresAt = data.expiresAt;

  const res = await fetch(`${API_URL}/v1/admin/invite-codes`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Erro ao criar código: ${res.status}`);
  return res.json();
}

async function toggleInviteCode(
  id: string,
  active: boolean
): Promise<InviteCode> {
  const res = await fetch(`${API_URL}/v1/admin/invite-codes/${id}`, {
    method: "PATCH",
    headers: getHeaders(),
    body: JSON.stringify({ active }),
  });
  if (!res.ok) throw new Error(`Erro ao atualizar código: ${res.status}`);
  return res.json();
}

async function fetchInviteCodeUses(
  id: string
): Promise<InviteCodeUsesResponse> {
  const res = await fetch(`${API_URL}/v1/admin/invite-codes/${id}/uses`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Erro ao buscar usos: ${res.status}`);
  return res.json();
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdminConvites() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // List state
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateInviteCodeForm>({
    label: "",
    maxUses: 10,
    code: "",
    expiresAt: "",
  });

  // Expanded row state (for usage details)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [uses, setUses] = useState<InviteCodeUse[]>([]);
  const [loadingUses, setLoadingUses] = useState(false);

  // Clipboard feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Toggle loading
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // ========================================
  // Auth guard
  // ========================================

  useEffect(() => {
    if (!user) return;
    if (user.role !== "SUPER_ADMIN") {
      navigate("/auth", { replace: true });
    }
  }, [user, navigate]);

  // ========================================
  // Fetch codes
  // ========================================

  const loadCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchInviteCodes();
      setCodes(data.codes);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar códigos"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  // ========================================
  // Create code
  // ========================================

  const handleCreate = async () => {
    if (!form.label.trim()) {
      setCreateError("Label é obrigatório");
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      await createInviteCode({
        label: form.label.trim(),
        maxUses: form.maxUses,
        code: form.code.trim() || undefined,
        expiresAt: form.expiresAt || undefined,
      });
      setShowCreateDialog(false);
      setForm({ label: "", maxUses: 10, code: "", expiresAt: "" });
      await loadCodes();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Erro ao criar código"
      );
    } finally {
      setCreating(false);
    }
  };

  // ========================================
  // Toggle active
  // ========================================

  const handleToggle = async (code: InviteCode) => {
    try {
      setTogglingId(code.id);
      await toggleInviteCode(code.id, !code.active);
      await loadCodes();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao atualizar código"
      );
    } finally {
      setTogglingId(null);
    }
  };

  // ========================================
  // Expand row
  // ========================================

  const handleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setUses([]);
      return;
    }

    try {
      setExpandedId(id);
      setLoadingUses(true);
      const data = await fetchInviteCodeUses(id);
      setUses(data.uses);
    } catch (err) {
      console.error("Erro ao buscar usos:", err);
      setUses([]);
    } finally {
      setLoadingUses(false);
    }
  };

  // ========================================
  // Copy to clipboard
  // ========================================

  const handleCopy = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      console.error("Falha ao copiar");
    }
  };

  // ========================================
  // Helpers
  // ========================================

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  // ========================================
  // Render
  // ========================================

  if (user && user.role !== "SUPER_ADMIN") {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center">
            <Ticket className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-display">
              Códigos de Convite
            </h1>
            <p className="text-sm text-muted-foreground">
              Gerencie códigos de acesso para novos usuários
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Código
        </Button>
      </div>

      {/* Error */}
      {error && (
        <Card className="p-4 border-destructive bg-destructive/10">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Carregando...</span>
          </div>
        ) : codes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Ticket className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-lg font-medium">Nenhum código encontrado</p>
            <p className="text-sm">Crie seu primeiro código de convite</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Código</TableHead>
                <TableHead>Label</TableHead>
                <TableHead className="text-center">Usos</TableHead>
                <TableHead>Expiração</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((code) => (
                <>
                  <TableRow
                    key={code.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleExpand(code.id)}
                  >
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <span>{code.code}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(code.code, code.id);
                          }}
                          title="Copiar código"
                        >
                          {copiedId === code.id ? (
                            <Check className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>{code.label}</TableCell>
                    <TableCell className="text-center">
                      <span
                        className={
                          code.remaining === 0
                            ? "text-destructive font-medium"
                            : ""
                        }
                      >
                        {code.usedCount}/{code.maxUses}
                      </span>
                    </TableCell>
                    <TableCell>
                      {code.expiresAt ? (
                        <span
                          className={
                            isExpired(code.expiresAt)
                              ? "text-destructive"
                              : ""
                          }
                        >
                          {formatDate(code.expiresAt)}
                          {isExpired(code.expiresAt) && (
                            <Badge variant="destructive" className="ml-2 text-xs">
                              Expirado
                            </Badge>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          Sem expiração
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {code.active ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30">
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpand(code.id);
                          }}
                          title="Ver usos"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(code);
                          }}
                          disabled={togglingId === code.id}
                          title={code.active ? "Desativar" : "Ativar"}
                        >
                          {togglingId === code.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : code.active ? (
                            <ToggleRight className="h-4 w-4 text-green-500" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                        {expandedId === code.id ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {/* Expanded usage details */}
                  {expandedId === code.id && (
                    <TableRow key={`${code.id}-uses`}>
                      <TableCell colSpan={6} className="bg-muted/30 p-4">
                        {loadingUses ? (
                          <div className="flex items-center gap-2 py-4 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Carregando usos...
                            </span>
                          </div>
                        ) : uses.length === 0 ? (
                          <div className="text-center py-4 text-muted-foreground text-sm">
                            Nenhum uso registrado
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium mb-3">
                              Histórico de Usos ({uses.length})
                            </h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Projeto</TableHead>
                                  <TableHead>UF</TableHead>
                                  <TableHead>IP</TableHead>
                                  <TableHead>Data de Uso</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {uses.map((use) => (
                                  <TableRow key={use.id}>
                                    <TableCell className="text-sm">
                                      {use.projectName}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="text-xs">
                                        {use.projectUf}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-muted-foreground">
                                      {use.ipAddress}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {formatDate(use.usedAt)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Novo Código de Convite</DialogTitle>
            <DialogDescription>
              Crie um novo código para convidar usuários a experimentar a
              plataforma.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {createError && (
              <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-md p-3">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{createError}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="label">
                Label <span className="text-destructive">*</span>
              </Label>
              <Input
                id="label"
                placeholder="Ex: Campanha Lançamento"
                value={form.label}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, label: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxUses">Max. Usos</Label>
              <Input
                id="maxUses"
                type="number"
                min={1}
                value={form.maxUses}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    maxUses: parseInt(e.target.value) || 1,
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">
                Código{" "}
                <span className="text-muted-foreground text-xs">
                  (opcional, auto-gerado se vazio)
                </span>
              </Label>
              <Input
                id="code"
                placeholder="Ex: LAUNCH2026"
                value={form.code}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    code: e.target.value.toUpperCase(),
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiresAt">
                Data de Expiração{" "}
                <span className="text-muted-foreground text-xs">
                  (opcional)
                </span>
              </Label>
              <Input
                id="expiresAt"
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, expiresAt: e.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={creating}
            >
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Código
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
